import { createFileRoute, notFound } from '@tanstack/react-router';
import type { ComponentType } from 'react';

import { EquitiesIndex } from '#/components/index/EquitiesIndex';
import { ETFsIndex } from '#/components/index/ETFsIndex';
import { MutualFundsIndex } from '#/components/index/MutualFundsIndex';
import { SectorIndex } from '#/components/index/SectorIndex';
import type { CategoryIndexProps } from '#/components/index/types';
import { CategoryNavigation } from '#/components/layout/CategoryNavigation';
import { DevSpacer } from '#/components/layout/DevSpacer';
import { FullPageNotFound } from '#/components/ui/FullPageNotFound';
import { Spinner } from '#/components/ui/Spinner';
import { categoryContentOptions } from '#/content/queries';
import {
  getInteriorCategoryActiveHref,
  INTERIOR_NAV_CTA,
  INTERIOR_NAV_PRODUCT_TYPES,
  INTERIOR_NAV_SECTORS,
  isSupportedCategory,
} from '#/lib/category-navigation';
import { queryClient } from '#/lib/query-client';
import type { CategorySlug } from '#/types';

// Per-type index views
const CATEGORY_INDEX_VIEWS: Record<
  CategorySlug,
  ComponentType<CategoryIndexProps>
> = {
  // product types
  equities: EquitiesIndex,
  etfs: ETFsIndex,
  'mutual-funds': MutualFundsIndex,

  // sectors
  'basic-materials': SectorIndex,
  'consumer-cyclical': SectorIndex,
  'consumer-defensive': SectorIndex,
  'financial-services': SectorIndex,
  'real-estate': SectorIndex,
  technology: SectorIndex,
  utilities: SectorIndex,
  'communication-services': SectorIndex,
  energy: SectorIndex,
  healthcare: SectorIndex,
  industrials: SectorIndex,
};

export const Route = createFileRoute('/$category/')({
  // Warm the category content cache before render so the first paint has
  // final data; section nav uses content to determine available sections.
  pendingComponent: () => <Spinner />,
  loader: async ({ params }) => {
    const { category } = params;

    if (!isSupportedCategory(category)) {
      throw notFound();
    }

    await queryClient
      .ensureQueryData(categoryContentOptions(category))
      .catch(() => null);

    return { category };
  },
  notFoundComponent: () => <FullPageNotFound message="Category not found." />,
  component: CategoryPage,
});

function CategoryPage() {
  const { category } = Route.useLoaderData();
  const IndexComponent = CATEGORY_INDEX_VIEWS[category];
  const activeHref = getInteriorCategoryActiveHref(category);

  return (
    <>
      <DevSpacer />
      <CategoryNavigation
        productTypes={INTERIOR_NAV_PRODUCT_TYPES}
        sectors={INTERIOR_NAV_SECTORS}
        cta={INTERIOR_NAV_CTA}
        activeHref={activeHref}
        enableSearchPreview
      />
      <IndexComponent category={category} />
    </>
  );
}
